import React, {useState} from "react";
import {
    Button, checkAndSetError, clearInputFormErrorFields,
    convertInputFormToObject,
    FileUploadButton,
    InputForm,
    inputFormIsValid, NumberField,
    SelectField,
    SwitchField,
    TextArea,
    TextField,
    DateTimePickerField,
    useInputForm
} from "../../../../Components/Inputs";
import {ownCatalogItemModel} from "./ownCatalogItemFactory";
import {GridCell, gridConstants} from "../../../../Components/Grid";
import {LOAN_LIBRARY_MEDIA_FORMAT_LIST} from "../../LoanLibrary/loanLibraryConstants";
import {error as notifyError} from "../../../../Components/Notifications/NotifyUser/notifyUserUtility";
import type {ListItem} from "../../../../Utilities/Types/listUtilities";

interface OwnCatalogItemViewProps {
    handleSaveOwnCatalogItem: (data: Record<string, unknown>) => void;
    handleReturn: () => void;
    ownCatalogItem: Record<string, any>;
    manufacturerList: ListItem[];
    vendorList: ListItem[];
}

export const OwnCatalogItemView = ({
                                    handleSaveOwnCatalogItem,
                                    handleReturn,
                                    ownCatalogItem,
                                    manufacturerList,
                                    vendorList,
                                }: OwnCatalogItemViewProps) => {

    const ownCatalogItemForm = useInputForm(ownCatalogItemModel(ownCatalogItem));

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(
        ownCatalogItem.productImageUrl || null
    );
    const [removeImage, setRemoveImage] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(0);

    const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp"];
    const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            notifyError("Invalid image type. Allowed: JPEG, PNG, GIF, WebP, BMP.");
            e.target.value = "";
            return;
        }

        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            notifyError("Image must be 2 MB or smaller.");
            e.target.value = "";
            return;
        }

        setImageFile(file);
        setImagePreviewUrl(URL.createObjectURL(file));
        setRemoveImage(false);
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreviewUrl(null);
        setRemoveImage(true);
        setFileInputKey(prev => prev + 1);
    };

    const handleInternalUseSwitch = (event: unknown) => {
        const updatedForm = {...ownCatalogItemForm};
        updatedForm.isForInternalUse.value = String((event as React.ChangeEvent<HTMLInputElement>).target.checked);
        ownCatalogItemForm.setValue(updatedForm);
    };

    const handleSubmitClick = () => {

        clearInputFormErrorFields(ownCatalogItemForm);

        checkAndSetError([
            ownCatalogItemForm.catalogNumber,
            ownCatalogItemForm.title,
            ownCatalogItemForm.objectType,
            ownCatalogItemForm.description,
            ownCatalogItemForm.orderLimit,
            ownCatalogItemForm.cost
            ]);

        if (inputFormIsValid(ownCatalogItemForm)) {
            const convertedFormData = convertInputFormToObject(ownCatalogItemForm);
            const ownCatalogItemData = ownCatalogItemModel(convertedFormData) as Record<string, unknown>;

            if (imageFile) {
                ownCatalogItemData.productImage = imageFile;
            }
            if (removeImage) {
                ownCatalogItemData.removeImage = true;
            }

            handleSaveOwnCatalogItem(ownCatalogItemData);
        }
    }

    return <>
        <InputForm name={`ownCatalogItemForm`}>
            <TextField {...ownCatalogItemForm.catalogNumber} medium_columns={gridConstants.column.SIX}/>
            <TextField {...ownCatalogItemForm.title} medium_columns={gridConstants.column.SIX}/>
            <SelectField {...ownCatalogItemForm.objectType} label="Object Type" options={LOAN_LIBRARY_MEDIA_FORMAT_LIST} includeDefaultOption medium_columns={gridConstants.column.SIX}/>
            <TextArea {...ownCatalogItemForm.description} medium_columns={gridConstants.column.TWELVE}/>
            <NumberField {...ownCatalogItemForm.orderLimit} isPositiveNumberOnly isWholeNumberOnly medium_columns={gridConstants.column.SIX}/>
            <NumberField {...ownCatalogItemForm.cost} isPositiveNumberOnly medium_columns={gridConstants.column.SIX}/>
            <TextField {...ownCatalogItemForm.fileLink} medium_columns={gridConstants.column.SIX}/>
            <TextField {...ownCatalogItemForm.keywords} label="Keywords/Tags" medium_columns={gridConstants.column.SIX}/>
            <SwitchField
                {...ownCatalogItemForm.isForInternalUse}
                name="toggleIsForInternalUse"
                label="Is For Internal Use"
                legend="Is For Internal Use"
                onChange={handleInternalUseSwitch}
                medium_columns={gridConstants.column.SIX}
            />

            <hr/>
            <SelectField
                {...ownCatalogItemForm.manufacturerId}
                label="Manufacturer"
                defaultOption="Select Manufacturer"
                defaultOptionValue=""
                options={manufacturerList}
                medium_columns={gridConstants.column.SIX}
            />
            <SelectField
                {...ownCatalogItemForm.vendorId}
                label="Vendor"
                defaultOption="Select Vendor"
                defaultOptionValue=""
                options={vendorList}
                medium_columns={gridConstants.column.SIX}
            />

            <hr/>
            <GridCell medium_columns={gridConstants.column.TWELVE}>
                <h3>Product Image</h3>
            </GridCell>
            <GridCell medium_columns={gridConstants.column.SIX}>
                {imagePreviewUrl && (
                    <div className="loanLibraryCatalogItem__image-preview">
                        <img
                            src={imagePreviewUrl}
                            alt="Product"
                            className="loanLibraryCatalogItem__image-preview-img"
                        />
                    </div>
                )}
                <div>
                    <div className="loanLibraryCatalogItem__file-upload-btn">
                        <FileUploadButton
                            key={fileInputKey}
                            name="productImage"
                            label={imagePreviewUrl ? "Replace Image" : "Upload Image"}
                            accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                            onChange={handleImageChange}
                        />
                    </div>
                    {imagePreviewUrl && (
                        <Button
                            isCollapsed
                            label="Remove Image"
                            name="btnRemoveImage"
                            onClick={handleRemoveImage}
                        />
                    )}
                </div>
            </GridCell>

            <hr/>
            <GridCell medium_columns={gridConstants.column.TWELVE}>
                <h3>What&apos;s New Settings</h3>
            </GridCell>
            <DateTimePickerField {...ownCatalogItemForm.whatsNewStartDate} medium_columns={gridConstants.column.SIX}/>
            <DateTimePickerField {...ownCatalogItemForm.whatsNewEndDate} medium_columns={gridConstants.column.SIX}/>

            <hr/>
            <GridCell className={`text-center`}>
                <Button isPrimary isCollapsed label={'Save'} name={`btnSubmit`}
                        onClick={handleSubmitClick}/>
                <Button isCollapsed label={'Cancel'} name={`btnCancel`} onClick={handleReturn}/>
            </GridCell>
        </InputForm>
    </>;
};
